/* =====================================================================
 * xlsxChartSupport — XLSX 차트 파트를 렌더 가능한 PptxChart 로 만드는 보조층
 *
 * 차트 파트(c:chartSpace) 문법 자체는 PPTX 와 동일해 parseChartPart 를 그대로
 * 쓰지만, 두 포맷은 데이터를 싣는 방식이 다르다. PPTX 는 워크북이 외부라 항상
 * numCache/strCache 를 함께 싣는 반면 XLSX 차트는 같은 패키지의 셀을 가리키므로
 * 캐시가 선택 사항이고, openpyxl 로 만든 차트에는 아예 없다 (c:f 참조식만 있음).
 * 그래서 여기서 참조식을 시트 값으로 풀어 채운다.
 *
 * 색도 마찬가지로 XLSX 차트는 대개 spPr 을 비워 두고 xl/theme/theme1.xml 의
 * accent 순환에 맡긴다.
 * ===================================================================== */

import type { ChartColorResolver, PptxChart, PptxChartSeries } from './pptxChartParser';

const A_NS = 'http://schemas.openxmlformats.org/drawingml/2006/main';

/** Office 기본 accent1~6 — 테마 파트가 없거나 읽지 못했을 때의 폴백 */
export const OOXML_DEFAULT_ACCENTS: readonly string[] = [
  '#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47',
];

const SCHEME_ALIASES: Record<string, string> = {
  tx1: 'dk1', bg1: 'lt1', tx2: 'dk2', bg2: 'lt2',
};

/** clrScheme 자식 하나(a:accent1 등) → hex. srgbClr / sysClr 만 다룬다. */
function schemeSlotColor(slot: Element | null): string | undefined {
  if (!slot) return undefined;
  const srgb = slot.getElementsByTagNameNS(A_NS, 'srgbClr')[0];
  if (srgb) {
    const val = srgb.getAttribute('val');
    if (val) return `#${val}`.toUpperCase();
  }
  const sys = slot.getElementsByTagNameNS(A_NS, 'sysClr')[0];
  if (sys) {
    const last = sys.getAttribute('lastClr');
    if (last) return `#${last}`.toUpperCase();
  }
  return undefined;
}

/** `xl/theme/theme1.xml` → accent1~6 hex. 슬롯이 비면 Office 기본값으로 메운다. */
export function parseThemeAccents(themeXml: string | null | undefined): string[] {
  if (!themeXml) return [...OOXML_DEFAULT_ACCENTS];
  let scheme: Element | null = null;
  try {
    const doc = new DOMParser().parseFromString(themeXml, 'application/xml');
    scheme = doc.getElementsByTagNameNS(A_NS, 'clrScheme')[0] ?? null;
  } catch {
    return [...OOXML_DEFAULT_ACCENTS];
  }
  if (!scheme) return [...OOXML_DEFAULT_ACCENTS];
  return OOXML_DEFAULT_ACCENTS.map((fallback, i) =>
    schemeSlotColor(scheme.getElementsByTagNameNS(A_NS, `accent${i + 1}`)[0] ?? null) ?? fallback);
}

/**
 * parseChartPart 에 주입할 색 해석기. XLSX 차트는 srgbClr 직접 지정 아니면 테마
 * 참조가 거의 전부라 그 둘만 처리하고, 나머지는 undefined 로 두어 렌더러의
 * accent 순환에 맡긴다.
 */
export function createThemeColorResolver(accents: readonly string[]): ChartColorResolver {
  return (el: Element): string | undefined => {
    const srgb = el.getElementsByTagNameNS(A_NS, 'srgbClr')[0];
    if (srgb) {
      const val = srgb.getAttribute('val');
      if (val) return `#${val}`.toUpperCase();
    }
    const schemeEl = el.getElementsByTagNameNS(A_NS, 'schemeClr')[0];
    const raw = schemeEl?.getAttribute('val');
    if (!raw) return undefined;
    const name = SCHEME_ALIASES[raw] ?? raw;
    const accent = /^accent([1-6])$/.exec(name);
    if (accent) return accents[Number(accent[1]) - 1];
    if (name === 'dk1' || name === 'dk2') return '#000000';
    if (name === 'lt1' || name === 'lt2') return '#FFFFFF';
    return undefined;
  };
}

/** 0-based, 양끝 포함 */
export interface XlsxChartRange {
  /** 참조식에 시트명이 없으면 undefined (차트가 놓인 시트로 해석) */
  sheet?: string;
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

/** `$B$2` / `B2` → 0-based 좌표 */
function parseA1(ref: string): { row: number; col: number } | null {
  const m = /^\$?([A-Za-z]{1,3})\$?(\d{1,7})$/.exec(ref.trim());
  if (!m) return null;
  const letters = m[1].toUpperCase();
  let col = 0;
  for (const ch of letters) col = col * 26 + (ch.charCodeAt(0) - 64);
  const row = Number(m[2]);
  if (col <= 0 || row <= 0) return null;
  return { row: row - 1, col: col - 1 };
}

/**
 * `'Report'!$B$2:$B$5` / `Sheet1!B1` → 범위. 시트명의 `''` 는 작은따옴표 이스케이프.
 * 다중 영역(`(A1:A2,B1:B2)`)이나 이름 정의는 지원하지 않는다 (null).
 */
export function parseChartRange(formula: string | undefined): XlsxChartRange | null {
  if (!formula) return null;
  const trimmed = formula.trim();
  if (!trimmed || trimmed.includes(',') || trimmed.includes('(')) return null;

  let sheet: string | undefined;
  let rangePart = trimmed;
  const bang = /^(?:'((?:[^']|'')*)'|([^'!]*))!(.+)$/.exec(trimmed);
  if (bang) {
    sheet = (bang[1] !== undefined ? bang[1].replace(/''/g, "'") : bang[2]) || undefined;
    rangePart = bang[3];
  }

  const [startRef, endRef] = rangePart.split(':');
  const start = parseA1(startRef ?? '');
  if (!start) return null;
  const end = endRef ? parseA1(endRef) : start;
  if (!end) return null;
  return {
    sheet,
    startRow: Math.min(start.row, end.row),
    startCol: Math.min(start.col, end.col),
    endRow: Math.max(start.row, end.row),
    endCol: Math.max(start.col, end.col),
  };
}

export interface XlsxChartCell {
  /** 표시 문자열 — 카테고리·계열명·제목용 */
  text: string;
  /** 수치 — 숫자가 아니면 null */
  value: number | null;
}

export type XlsxChartCellReader = (
  sheet: string | undefined,
  row: number,
  col: number,
) => XlsxChartCell | undefined;

/** 범위를 셀 단위로 훑는다. 행/열 벡터 모두 한 줄로 펴서 돌려준다. */
function readRange(range: XlsxChartRange, read: XlsxChartCellReader): XlsxChartCell[] {
  const out: XlsxChartCell[] = [];
  for (let row = range.startRow; row <= range.endRow; row++) {
    for (let col = range.startCol; col <= range.endCol; col++) {
      out.push(read(range.sheet, row, col) ?? { text: '', value: null });
    }
  }
  return out;
}

function readSingle(formula: string | undefined, read: XlsxChartCellReader): string | undefined {
  const range = parseChartRange(formula);
  if (!range) return undefined;
  return read(range.sheet, range.startRow, range.startCol)?.text || undefined;
}

function hydrateSeries(series: PptxChartSeries, read: XlsxChartCellReader): PptxChartSeries {
  // 캐시가 있으면 그대로 신뢰한다 — 실제 셀보다 차트가 만들어진 시점의 값이 맞다.
  const next: PptxChartSeries = { ...series };
  if (next.values.length === 0) {
    const range = parseChartRange(next.valuesRef);
    if (range) next.values = readRange(range, read).map((cell) => cell.value);
  }
  if (next.categories.length === 0) {
    const range = parseChartRange(next.categoriesRef);
    if (range) next.categories = readRange(range, read).map((cell) => cell.text);
  }
  if (next.name == null) next.name = readSingle(next.nameRef, read);
  return next;
}

/**
 * 캐시가 비어 있는 계열/카테고리/이름/제목을 시트 값으로 채운다.
 * 채울 게 없으면 원본을 그대로 돌려준다.
 */
export function hydrateXlsxChart(chart: PptxChart, read: XlsxChartCellReader): PptxChart {
  const series = chart.series.map((s) => hydrateSeries(s, read));
  const title = chart.title ?? readSingle(chart.titleRef, read);
  return { ...chart, series, title };
}

/** 계열에 그릴 수치가 하나도 없으면 렌더러가 빈 축만 그리므로 아예 숨긴다. */
export function chartHasPlottableData(chart: PptxChart): boolean {
  return chart.series.some((s) => s.values.some((v) => v !== null));
}
